import React, { useState, useMemo } from 'react';
// NOTE: This embed page is DISPLAY-ONLY - NO wallet connection allowed in iframes
// All wallet flows must use full-page routes (/verify, /register-qr)
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Shield, CheckCircle, Loader2, AlertCircle, Users, Ban, Gift, Twitter, Copy, Clock, ArrowLeft, Youtube, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EmbedVerifyPage = () => {
  // NO useAccount() - this page must work without wallet connection in iframe
  
  // Scout referral — capture ?ref=0xWALLET from URL
  const referralWallet = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && /^0x[a-fA-F0-9]{40}$/.test(ref)) return ref.toLowerCase();
    return null;
  }, []);
  
  const [platform, setPlatform] = useState('twitter');
  const [socialHandle, setSocialHandle] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [bscAddress, setBscAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationData, setVerificationData] = useState(null);
  const [status, setStatus] = useState(null);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [submittedForReview, setSubmittedForReview] = useState(false);
  const [forceShowForm, setForceShowForm] = useState(false);  // Override verified redirect
  // Optional email for CRM (never shared publicly)
  const [contactEmail, setContactEmail] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);

  // NO wallet auto-fill - user must enter address manually in embed mode
  // This ensures the page works in cross-origin iframes without wallet issues

  // Check status on mount - only if user has entered an address
  const checkStatus = React.useCallback(async (walletToCheck) => {
    if (!walletToCheck) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/verification/status/${walletToCheck}`);
      setStatus(response.data);
      setHasCheckedStatus(true);
    } catch (error) {
      console.error('Error checking status:', error);
      setHasCheckedStatus(true);
    }
  }, []);

  // Manual status check when user enters address (no auto-check without wallet)

  const handlePlatformChange = (newPlatform) => {
    setPlatform(newPlatform);
    setSocialHandle('');
    setYoutubeUrl('');
    setVerificationCode('');
    setVerificationData(null);
    setSubmittedForReview(false);
    setContactEmail('');
    setEmailOptIn(false);
  };

  const handleGoBack = () => {
    setVerificationCode('');
    setVerificationData(null);
    setStatus(null);
    setHasCheckedStatus(false);
    setSocialHandle('');
    setYoutubeUrl('');
    setSubmittedForReview(false);
    setForceShowForm(true);  // Force show form even if verified
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bscAddress.trim()) {
      toast.error('Please enter your BSC address');
      return;
    }
    
    if (!/^0x[a-fA-F0-9]{40}$/.test(bscAddress.trim())) {
      toast.error('Invalid BSC address format');
      return;
    }
    
    if (platform === 'twitter' && !socialHandle.trim()) {
      toast.error('Please enter your X handle');
      return;
    }
    
    if (platform === 'youtube' && !youtubeUrl.trim()) {
      toast.error('Please enter your YouTube channel or @handle');
      return;
    }
    
    try {
      setLoading(true);
      
      let response;
      
      if (platform === 'twitter') {
        response = await axios.post(`${BACKEND_URL}/api/verification/request`, {
          wallet: bscAddress.trim(),
          socialHandle: socialHandle.trim(),
          platform: 'twitter',
          contactEmail: contactEmail.trim() || null,
          emailOptIn: emailOptIn,
          referralWallet: referralWallet || undefined,
        });
      } else if (platform === 'youtube') {
        response = await axios.post(`${BACKEND_URL}/api/verification/youtube/request`, {
          wallet: bscAddress.trim(),
          channelUrl: (() => { const v = youtubeUrl.trim(); return (v && !v.startsWith('@') && !v.includes('youtube.com') && !v.includes('http')) ? `@${v}` : v; })(),
          contactEmail: contactEmail.trim() || null,
          emailOptIn: emailOptIn,
          referralWallet: referralWallet || undefined,
        });
        response.data.verificationCode = response.data.verification_code || response.data.verificationCode;
      }
      
      // Check if already verified
      if (response.data.alreadyVerified) {
        toast.success(`This ${platform === 'twitter' ? 'X' : 'YouTube'} account is already verified with this wallet. No action needed!`, {
          duration: 5000
        });
        setSubmittedForReview(true);
        return;
      }
      
      // Show replacement warning if applicable
      if (response.data.isReplacement) {
        toast.warning(response.data.warning || 'You are changing the wallet for this account. The previous wallet will be replaced after approval.');
      }
      
      setVerificationCode(response.data.verificationCode);
      setVerificationData(response.data);
      toast.success('Verification code generated!');
      
    } catch (error) {
      console.error('Verification error:', error);
      const errorDetail = error.response?.data?.detail || '';
      const statusCode = error.response?.status;
      
      // Handle rate limiting (429)
      if (statusCode === 429) {
        toast.error(errorDetail || 'Too many requests. Please wait a few minutes before trying again.');
        return;
      }
      
      // Handle specific error cases with user-friendly messages
      if (errorDetail.includes('PENDING_SAME_WALLET')) {
        toast.error('You already have a pending verification for this channel. Please wait for it to be reviewed.');
      } else if (errorDetail.includes('PENDING_DIFFERENT_WALLET')) {
        toast.error('This channel has a pending verification from another wallet. Please wait for that to be reviewed first.');
      } else if (errorDetail.includes('PENDING_ALREADY_EXISTS')) {
        toast.error('This YouTube channel already has a verification pending. Please wait for it to be reviewed before submitting again.');
      } else if (errorDetail.includes('ALREADY_VERIFIED_SAME_WALLET')) {
        toast.info('This account is already verified with this wallet. No action needed!');
      } else if (errorDetail.includes('ALREADY_VERIFIED') || errorDetail.toLowerCase().includes('already verified')) {
        // This shouldn't normally happen since backend allows replacement
        // But if it does, explain the situation
        toast.info('This account is already verified with another wallet. Submit with your new wallet to request a transfer (requires admin approval).');
      } else {
        toast.error(errorDetail || 'Failed to request verification');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    try {
      setSubmitting(true);
      
      // Merge referral at submit time (URL param as only source for embed)
      const resolvedReferral = referralWallet || null;
      console.log('[EMBED SUBMIT-FOR-REVIEW PAYLOAD FULL]', {
        wallet: bscAddress.trim(),
        platform,
        referralWallet: resolvedReferral,
      });
      
      // Submit for review - always include referral if available
      const payload = {
        wallet: bscAddress.trim(),
        platform: platform,
      };
      if (resolvedReferral) {
        payload.referralWallet = resolvedReferral;
      }
      await axios.post(`${BACKEND_URL}/api/verification/submit-for-review`, payload);
      
      setSubmittedForReview(true);
      toast.success('Request submitted for review!');
      
    } catch (error) {
      console.error('Submit error:', error);
      // Even if endpoint fails, show success (request is already pending from code generation)
      setSubmittedForReview(true);
      toast.success('Request submitted for review!');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(verificationCode)
        .then(() => toast.success('Verification code copied!'))
        .catch((err) => {
          console.error('Clipboard API failed:', err);
          fallbackCopy();
        });
    } else {
      fallbackCopy();
    }
  };
  
  const fallbackCopy = () => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = verificationCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Verification code copied!');
      } else {
        toast.error('Failed to copy - please copy manually');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      toast.error('Failed to copy - please copy manually');
    }
  };

  // Verified state - but allow user to add more accounts
  if (status?.verified && hasCheckedStatus && !verificationCode && !forceShowForm) {
    // Get list of verified platforms
    const verifiedPlatforms = status?.verifiedPlatforms || [];
    
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-8">
        <div className="max-w-xl mx-auto">
          <Card className="p-10 text-center bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
            <div className="w-20 h-20 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-3 text-[#1a1a1a]">Verified!</h2>
            <p className="text-[#666] text-lg mb-8">Your wallet is verified and eligible for TRUE Points rewards.</p>
            <div className="bg-[#d4cfc4] rounded-xl p-6 text-left space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-[#1a1a1a]">Send & receive tips</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-[#1a1a1a]">Earn TRUE Points weekly</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-[#1a1a1a]">Claim TIP token rewards</span>
              </div>
            </div>
            <Button 
              onClick={handleGoBack} 
              className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
              data-testid="add-another-account-btn"
            >
              Add Another Social Account
            </Button>
            <p className="text-xs text-[#888] mt-3">
              Link additional social accounts or change wallet for existing ones
            </p>
          </Card>
        </div>
      </div>
    );
  }

  // Pending review state (from status check)
  if (status?.submittedForReview && !verificationCode && hasCheckedStatus && !forceShowForm) {
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-8">
        <div className="max-w-xl mx-auto">
          <Card className="p-10 text-center bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
            <div className="w-20 h-20 bg-[#f0b90b] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-3 text-[#1a1a1a]">Pending Review</h2>
            <p className="text-[#666] text-lg mb-4">Your verification request is being reviewed by our team</p>
            <p className="text-[#888] text-sm mb-6">This usually takes less than 24 hours</p>
            <Button 
              onClick={handleGoBack} 
              className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
            >
              Add Another Social Account
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // Submitted for review state (after clicking submit button)
  if (submittedForReview) {
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-8">
        <div className="max-w-xl mx-auto">
          <Card className="p-10 text-center bg-[#e8e3db] border-0 shadow-lg rounded-2xl" data-testid="verification-success">
            <div className="w-20 h-20 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-3 text-[#1a1a1a]">Request Submitted</h2>
            <p className="text-[#666] text-lg mb-4">Your verification request is now in the review queue.</p>
            
            <div className="bg-[#d4cfc4] rounded-xl p-4 mb-4">
              <div className="flex items-center justify-center gap-2 text-[#1a1a1a]">
                <Clock className="w-4 h-4" />
                <span className="font-semibold">Status: Pending</span>
              </div>
            </div>
            
            <Alert className="mb-6 border-[#d4cfc4] bg-[#d4cfc4]/50 text-left">
              <AlertCircle className="h-4 w-4 text-[#666]" />
              <AlertDescription className="text-[#666] text-sm">
                <strong>Important:</strong> Keep the verification code visible on your {platform === 'twitter' ? 'X profile' : 'YouTube channel'} until you receive approval.
                <br /><br />
                Manual review typically takes <strong>less than 24 hours</strong>.
              </AlertDescription>
            </Alert>
            
            <button 
              onClick={handleGoBack} 
              className="text-sm text-[#666] hover:text-[#1a1a1a] underline underline-offset-2"
              data-testid="start-another-verification"
            >
              Start another verification
            </button>
          </Card>
        </div>
      </div>
    );
  }

  // Platform-specific instructions
  const renderInstructions = () => {
    if (platform === 'twitter') {
      return (
        <>
          <div className="bg-[#d4cfc4] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-[#f0b90b] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">2</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Post on X (Twitter)</span>
            </div>
            <p className="text-[#666] text-sm ml-11">
              Post a <strong className="text-[#1a1a1a]">public tweet</strong> containing your verification code.
            </p>
          </div>

          <div className="bg-[#d4cfc4] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-[#f0b90b] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">3</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Submit for Review</span>
            </div>
            <p className="text-[#666] text-sm ml-11">
              Click the button below to add your request to the review queue.
            </p>
          </div>
        </>
      );
    } else if (platform === 'youtube') {
      return (
        <>
          <div className="bg-[#d4cfc4] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-[#f0b90b] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">2</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Add to your YouTube channel</span>
            </div>
            <div className="text-[#666] text-sm ml-11 space-y-2">
              <p>Add this code to <strong className="text-[#1a1a1a]">ONE</strong> of:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Channel About section</strong> (recommended)</li>
                <li><strong>Any video description</strong></li>
              </ul>
            </div>
          </div>

          <Alert className="border-red-300 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-700 text-sm">
              <strong>Comments are NOT accepted</strong> — only About section or video descriptions.
            </AlertDescription>
          </Alert>

          <div className="bg-[#d4cfc4] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-[#f0b90b] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">3</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Submit for Review</span>
            </div>
            <p className="text-[#666] text-sm ml-11">
              Click the button below to add your request to the review queue.
            </p>
          </div>
        </>
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#c8c3bb] p-8">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-[#f0b90b] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2">Get Verified</h1>
          <p className="text-[#666] text-lg">Link your social account to become eligible for rewards</p>
        </div>

        {/* Why Verify Section */}
        <Card className="p-6 mb-6 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
          <h3 className="text-xl font-bold mb-5 text-[#1a1a1a]">Why Verify?</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#d4cfc4] rounded-xl flex items-center justify-center flex-shrink-0">
                <Ban className="w-5 h-5 text-[#f0b90b]" />
              </div>
              <div>
                <p className="font-semibold text-[#1a1a1a]">Prevent Fraud</p>
                <p className="text-[#666] text-sm">Stops bots and fake accounts from gaming the system</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#d4cfc4] rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-[#f0b90b]" />
              </div>
              <div>
                <p className="font-semibold text-[#1a1a1a]">Real Community</p>
                <p className="text-[#666] text-sm">Only genuine creators and supporters earn rewards</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#d4cfc4] rounded-xl flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-[#f0b90b]" />
              </div>
              <div>
                <p className="font-semibold text-[#1a1a1a]">Fair Distribution</p>
                <p className="text-[#666] text-sm">Rewards go to real participants, not sybil attackers</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Rejection Alert */}
        {status?.rejectionReason && (
          <Alert className="mb-6 border-red-300 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-700">
              Your previous request was rejected: {status.rejectionReason}
            </AlertDescription>
          </Alert>
        )}

        {/* Main Form Card */}
        <Card className="p-8 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
          {!verificationCode ? (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label className="text-sm font-semibold text-[#1a1a1a] mb-2 block">Platform</Label>
                <Select value={platform} onValueChange={handlePlatformChange}>
                  <SelectTrigger className="h-12 bg-[#d4cfc4] border-0 rounded-xl text-[#1a1a1a]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twitter">
                      <div className="flex items-center gap-2">
                        <Twitter className="w-4 h-4" />
                        X (Twitter)
                      </div>
                    </SelectItem>
                    <SelectItem value="youtube">
                      <div className="flex items-center gap-2">
                        <Youtube className="w-4 h-4" />
                        YouTube
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {platform === 'twitter' ? (
                <div>
                  <Label className="text-sm font-semibold text-[#1a1a1a] mb-2 block">Your X Handle</Label>
                  <Input
                    placeholder="@username"
                    value={socialHandle}
                    onChange={(e) => setSocialHandle(e.target.value)}
                    className="h-12 bg-[#d4cfc4] border-0 rounded-xl text-[#1a1a1a] placeholder:text-[#999]"
                  />
                  <p className="text-xs text-[#888] mt-2">Enter your X (Twitter) username</p>
                </div>
              ) : (
                <div>
                  <Label className="text-sm font-semibold text-[#1a1a1a] mb-2 block">Your YouTube Channel</Label>
                  <Input
                    placeholder="@MrBeast or youtube.com/@MrBeast"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    className="h-12 bg-[#d4cfc4] border-0 rounded-xl text-[#1a1a1a] placeholder:text-[#999]"
                  />
                  <p className="text-xs text-[#888] mt-2">
                    Paste your channel URL or @handle. We'll take care of the rest.
                  </p>
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold text-[#1a1a1a] mb-2 block">BSC Wallet Address</Label>
                <Input
                  placeholder="0x..."
                  value={bscAddress}
                  onChange={(e) => setBscAddress(e.target.value)}
                  className="h-12 bg-[#d4cfc4] border-0 rounded-xl font-mono text-sm text-[#1a1a1a] placeholder:text-[#999]"
                />
                <p className="text-xs text-[#888] mt-2">This is where you'll receive your TIP rewards</p>
              </div>

              {/* Optional Email for Updates */}
              <div className="border border-[#d4cfc4] rounded-xl p-4 bg-[#d4cfc4]/30">
                <Label className="text-sm font-semibold text-[#1a1a1a] mb-2 block">Contact Email (Optional)</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="h-12 bg-white border-0 rounded-xl text-[#1a1a1a] placeholder:text-[#999]"
                  data-testid="contact-email-input"
                />
                <div className="flex items-start gap-2 mt-3">
                  <input
                    type="checkbox"
                    id="emailOptIn"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    className="mt-1 rounded border-[#d4cfc4]"
                    data-testid="email-opt-in-checkbox"
                  />
                  <label htmlFor="emailOptIn" className="text-xs text-[#666] leading-relaxed">
                    I'd like to receive important updates about my verification status and TipMe features. 
                    <span className="block text-[#888] mt-1">
                      Your email is <strong>never shared publicly</strong> or with third parties.
                    </span>
                  </label>
                </div>
              </div>

              <Alert className="border-[#d4cfc4] bg-[#d4cfc4]/50">
                <Clock className="h-4 w-4 text-[#666]" />
                <AlertDescription className="text-[#666] text-sm">
                  Verification is <strong>manually reviewed</strong>. Approval typically takes <strong>up to 24 hours</strong>.
                </AlertDescription>
              </Alert>

              <Button type="submit" disabled={loading} className="w-full h-14 bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold text-base rounded-xl shadow-md transition-all">
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</> : 'Get Verification Code'}
              </Button>
            </form>
          ) : (
            /* Code Generated - Show Instructions */
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-7 h-7 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-[#1a1a1a]">Code Generated!</h3>
                <p className="text-[#666] text-sm mt-1">Follow the steps below</p>
              </div>

              {/* Step 1 - Copy Code */}
              <div className="bg-[#f0b90b]/10 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-[#f0b90b] rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <span className="font-semibold text-[#1a1a1a]">Copy your unique code</span>
                </div>
                <div className="flex gap-3">
                  <Input value={verificationCode} readOnly className="bg-white border-[#f0b90b] font-mono font-bold text-[#f0b90b]" onClick={(e) => e.target.select()} />
                  <Button onClick={copyCode} variant="outline" className="border-[#d4cfc4] hover:bg-[#d4cfc4]">
                    <Copy className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Platform-specific instructions */}
              {renderInstructions()}

              {/* Primary CTA - Submit for Review */}
              <Button 
                onClick={handleSubmitForReview} 
                disabled={submitting}
                className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold py-6 text-lg rounded-xl"
                data-testid="submit-for-review-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> I've added the code — Submit for review</>
                )}
              </Button>
              
              <p className="text-xs text-center text-[#888]">
                This will send your request to the admin review queue.<br />
                Keep the code visible until approved.
              </p>

              {/* Secondary action */}
              <button 
                onClick={handleGoBack} 
                className="text-sm text-[#666] hover:text-[#1a1a1a] underline underline-offset-2 w-full text-center"
                data-testid="start-another-verification-link"
              >
                Start another verification
              </button>
            </div>
          )}
        </Card>

        {/* How It Works */}
        <Card className="p-6 mt-6 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
          <h3 className="text-lg font-bold mb-4 text-[#1a1a1a]">How Verification Works</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-[#666] text-sm">Post a unique code to prove account ownership</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-[#666] text-sm">Our team verifies the code matches your wallet</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-[#666] text-sm">Once approved, you're eligible for rewards</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
